// searchService.js
// -----------------------------------------------------------------------------
// The reads behind ค้นหาการจอง. Every rule -- what the query is, which stored
// phone matches, how rows become orders -- is in orderSearch.js; this file only
// fetches, so the rules stay testable without Firestore.
//
// Each read can fail on its own. A failure is reported and named in `failed`,
// never swallowed into an empty result: an empty result is what the page shows
// as ไม่พบ, and telling a caller their order does not exist because a query
// failed is the one answer this page must never give.
//
// Firestore: equality and single-field range queries only, so no composite
// index is needed (CLAUDE.md -- NEVER deploy firestore.indexes.json).
// -----------------------------------------------------------------------------

import axios from 'axios'
import { db } from '@/firebase'
import O from '@/components/orderSearch'
import D from '@/components/driverDay'
import { reportError, reportWarning } from '@/components/report'

const SLOT_COL = () => db.collection('bookingSlot')
const BOOKING_COL = () => db.collection('booking')
const LIST_COL = () => db.collection('deliveryList')
const STOP_COL = () => db.collection('deliveryStop')

// A phone prefix on 88,000 deliveryList rows returns a handful; the cap only
// stops a pathological value from reading the collection.
const PER_QUERY_LIMIT = 200
// Firestore's `in` takes at most ten values.
const IN_LIMIT = 10
const SAP_TIMEOUT_MS = 10000

function rowsOf (snap) {
  const out = []
  snap.forEach(d => out.push(Object.assign({ id: d.id }, d.data())))
  return out
}

function concatAll (lists) {
  return lists.reduce((all, l) => all.concat(l), [])
}

function uniqueById (rows) {
  const seen = {}
  return rows.filter(r => {
    if (seen[r.id]) return false
    seen[r.id] = true
    return true
  })
}

function soNumbersOf (rows) {
  const seen = {}
  rows.forEach(r => {
    const so = r.soNo == null ? '' : String(r.soNo).trim()
    if (so) seen[so] = true
  })
  return Object.keys(seen)
}

async function byRt (col, rtNo) {
  return rowsOf(await col().where('rtNo', '==', rtNo).get())
}

async function bySo (col, soNos) {
  const chunks = []
  for (let i = 0; i < soNos.length; i += IN_LIMIT) chunks.push(soNos.slice(i, i + IN_LIMIT))
  const snaps = await Promise.all(chunks.map(c => col().where('soNo', 'in', c).get()))
  return concatAll(snaps.map(rowsOf))
}

// Every form in O.telPrefixes as a prefix range, then telMatches decides --
// the range also returns a longer number that merely starts the same way.
// A snapshot that comes back exactly at PER_QUERY_LIMIT may have been cut off
// by it, so the caller is told through `truncated` rather than left to guess.
async function byTelPrefix (col, field, digits) {
  const snaps = await Promise.all(O.telPrefixes(digits).map(p =>
    col().where(field, '>=', p).where(field, '<=', p + '\uf8ff').limit(PER_QUERY_LIMIT).get()))
  const truncated = snaps.some(s => s.size === PER_QUERY_LIMIT)
  const rows = uniqueById(concatAll(snaps.map(rowsOf))).filter(r => O.telMatches(r[field], digits))
  return { rows, truncated }
}

async function byIds (col, ids) {
  const snaps = await Promise.all(ids.map(id => col().doc(id).get()))
  return snaps.filter(s => s.exists).map(s => Object.assign({ id: s.id }, s.data()))
}

async function attempt (label, failed, fallback, fn) {
  try {
    return await fn()
  } catch (e) {
    reportError(`ค้นหาการจอง: reading ${label} failed:`, e)
    failed.push(label)
    return fallback
  }
}

async function stopsFor (lists) {
  const ids = lists.map(l => D.stopIdFor(l.deliveryDate, l.regNum, D.stopKeyFor(l)))
  const found = await byIds(STOP_COL, Array.from(new Set(ids)))
  const map = {}
  found.forEach(s => { map[s.id] = s })
  return map
}

async function getSo (webPath, soNo) {
  const res = await axios.post(`${webPath}deliveryWebService.asmx/GetSo`, { so: soNo }, { timeout: SAP_TIMEOUT_MS })
  const rows = JSON.parse(res.data.d)
  return Array.isArray(rows) && rows.length ? Object.assign({ soNo: soNo }, rows[0]) : null
}

// Never fatal: a card with no product lines is still the answer to the call.
async function getProdList (webPath, soNo) {
  try {
    const res = await axios.post(`${webPath}deliveryWebService.asmx/GetProdList`, { so: soNo }, { timeout: SAP_TIMEOUT_MS })
    const rows = JSON.parse(res.data.d)
    return Array.isArray(rows) ? rows : []
  } catch (e) {
    reportWarning('ค้นหาการจอง: GetProdList failed for SO ' + soNo + ':', e)
    return []
  }
}

export async function searchOrders (text, { webPath }) {
  const query = O.classifyQuery(text)
  const failed = []
  if (query.kind !== 'tel' && query.kind !== 'so') return { query, orders: [], failed, truncated: false }

  // Set when a capped phone read came back exactly at PER_QUERY_LIMIT -- the
  // result may then be missing rows an SO search would still find.
  let truncated = false
  let slots, bookings, lists
  if (query.kind === 'so') {
    // A typed number may be an SO or an RT; both are equality reads.
    const [s1, b1, l1, s2, b2, l2] = await Promise.all([
      attempt('slot', failed, [], () => bySo(SLOT_COL, [query.soNo])),
      attempt('booking', failed, [], () => bySo(BOOKING_COL, [query.soNo])),
      attempt('list', failed, [], () => bySo(LIST_COL, [query.soNo])),
      attempt('slot', failed, [], () => byRt(SLOT_COL, query.soNo)),
      attempt('booking', failed, [], () => byRt(BOOKING_COL, query.soNo)),
      attempt('list', failed, [], () => byRt(LIST_COL, query.soNo))
    ])
    slots = uniqueById(s1.concat(s2))
    bookings = uniqueById(b1.concat(b2))
    lists = uniqueById(l1.concat(l2))
    // An exchange found by its RT: follow its SO into the rows written before
    // the RT was attached, which carry no rtNo.
    const others = soNumbersOf(slots.concat(bookings, lists)).filter(n => n !== query.soNo)
    if (others.length) {
      const [s3, b3, l3] = await Promise.all([
        attempt('slot', failed, [], () => bySo(SLOT_COL, others)),
        attempt('booking', failed, [], () => bySo(BOOKING_COL, others)),
        attempt('list', failed, [], () => bySo(LIST_COL, others))
      ])
      slots = uniqueById(slots.concat(s3))
      bookings = uniqueById(bookings.concat(b3))
      lists = uniqueById(lists.concat(l3))
    }
  } else {
    const noRows = { rows: [], truncated: false }
    let bookingResult, listResult
    [slots, bookingResult, listResult] = await Promise.all([
      attempt('slot', failed, [], async () => rowsOf(await SLOT_COL().where('telDigits', '==', query.digits).get())),
      attempt('booking', failed, noRows, () => byTelPrefix(BOOKING_COL, 'telephone', query.digits)),
      attempt('list', failed, noRows, () => byTelPrefix(LIST_COL, 'custTel', query.digits))
    ])
    bookings = bookingResult.rows
    lists = listResult.rows
    truncated = bookingResult.truncated || listResult.truncated
    // Follow every SO found into the stages that stored the phone another way
    // (or not at all -- 40% of deliveryList rows have no custTel).
    const soNos = soNumbersOf(slots.concat(bookings, lists))
    if (soNos.length) {
      const [s2, b2, l2] = await Promise.all([
        attempt('slot', failed, [], () => bySo(SLOT_COL, soNos)),
        attempt('booking', failed, [], () => bySo(BOOKING_COL, soNos)),
        attempt('list', failed, [], () => bySo(LIST_COL, soNos))
      ])
      slots = uniqueById(slots.concat(s2))
      bookings = uniqueById(bookings.concat(b2))
      lists = uniqueById(lists.concat(l2))
    }
  }

  // A plan whose booking was not returned -- a นัดหมาย with no SO, found by a
  // phone the plan stored differently -- still joins it through sourceSlotId.
  const have = {}
  slots.forEach(s => { have[s.id] = true })
  const missing = Array.from(new Set(bookings.map(b => b.sourceSlotId).filter(id => id && !have[id])))
  if (missing.length) slots = slots.concat(await attempt('slot', failed, [], () => byIds(SLOT_COL, missing)))

  const stops = lists.length ? await attempt('stop', failed, {}, () => stopsFor(lists)) : {}
  let orders = O.mergeOrders({ slots, bookings, lists, stops })

  // SAP only answers "is this SO real and unbooked" when Firestore answered
  // cleanly that nothing here holds it. After a failed read it would be a guess.
  if (query.kind === 'so' && !orders.length && !failed.length) {
    const so = await attempt('sap', failed, null, () => getSo(webPath, query.soNo))
    if (so) orders = [O.sapOrder(so, await getProdList(webPath, query.soNo))]
  }

  return { query, orders, failed: Array.from(new Set(failed)), truncated }
}

export default { searchOrders }
